import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Breadcrumb, Button, Col, Form, Input, Radio, Row, Select, Space } from 'antd';
import eiqSecureStore from '../../stores/eiq-secure.store';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { FormWrapper } from '../../../../common/components/form-wrapper/form-wrapper';
import './add-visitor.scss';
import guestService from '../../../../common/services/guest-service/guest.service';
import { notification } from '../../../../common/utils/notification';
import { formatAddressLabel } from '../../../../common/helpers/address.helper';
import { useModal } from '../../../../common/hooks/use-modal/use-modal.hook';
import { RadioChangeEvent } from 'antd/lib';
import TurnbackModal from '../turnback-modal/turnback-modal';
import ContactsModal from '../contacts-modal/contacts-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import { GuestType } from 'common/enums/guest-type.enum';
import { addVisitorValidation } from './add-visitor.validation';
import { toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { useTableContext } from 'common/components/table/table-context';
import { TurnBack, Admit } from 'features/eiq-secure/constans/admit-actions.constants';
import { useMedia } from '../../../../common/hooks/use-media/use-media.hook';
import { AdmitGuestEnum } from '../../constans/admit-types';
import Footer from '../../../../common/components/footer/footer';
import { setResidentListSearchFilter, setVacantAddressSearchFilter } from 'common/helpers/search-filter.helper';
import EiqSecureGuestPrintInstruction from '../eiq-secure-guest-license/eiq-secure-guest-print-instruction';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { useJsApiLoader } from '@react-google-maps/api';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { Image } from 'antd';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import { base64ToFile } from 'common/helpers/getBase64.helper';
import accountService from 'common/services/file-service/file.service';
import featureStore from 'common/stores/features/feature.store';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import DrivingPrivilegesRevokeModal from '../driving-privileges-revoked-modal/driving-privileges-revoked-modal';
import DriveId from '../drive-id/drive-id';
import { useGuestPrintInstruction } from 'features/eiq-secure/hooks/use-guest-license-print';

interface IAddVisitorProps {
  isVacantAddress: boolean;
}

const createRouteOrigin = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): google.maps.LatLngLiteral | undefined => {
  if (typeof latitude !== 'number' || typeof longitude !== 'number' || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined;
  }

  return {
    lat: latitude,
    lng: longitude,
  };
};

export const AddVisitor = observer(({ isVacantAddress }: IAddVisitorProps) => {
  const getUnlistedGuestDefaultType = () => {
    const type = configurationStore.configuration?.default.guestType;

    if (type === GuestType.Family) {
      return GuestType.Guest;
    }

    return type;
  };

  const tableContext = useTableContext();
  const { isMobileOrTable } = useMedia();

  const selectedAction = useRef<string>();
  const turnbackReason = useRef<string>();
  const isDrivingPrivilagesRevoked = useRef(false);
  const idDocumentRef = useRef<any>(null);

  const [visitorType, setVisitorType] = useState<GuestType | undefined | null>(getUnlistedGuestDefaultType());
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true);
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<any>({});

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: null,
  });

  useBoundPlateRecognition(() => {
    const fallbackState = configurationStore.configuration?.default.defaultLicenseState;
    form.setFieldValue('carLicensePlate', '');
    form.setFieldValue('carState', fallbackState);
    form.setFieldValue('gate', userAuthStore.defaultGate || undefined);
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey });

  const resetState = () => {
    selectedAction.current = '';
    turnbackReason.current = '';
    isDrivingPrivilagesRevoked.current = false;
    idDocumentRef.current = null;

    form.resetFields();
  };

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  const gateOptions = toGateOptions(configurationStore?.configuration?.gates || []);

  const uploadIdDocumentImage = async (values: any) => {
    const idDocument = idDocumentRef.current?.getIdDocument();

    if (!idDocument) {
      return { url: '', type: '' };
    }

    const { firstName, lastName, type, imageBase64 } = idDocument;

    const first = type === 'snapshot' ? values.firstName : firstName;
    const last = type === 'snapshot' ? values.lastName : lastName;

    const documentNameFileName = `${first}_${last}_${type}.jpg`;
    const file = base64ToFile(imageBase64, documentNameFileName);

    if (file.size === 0) {
      notification.warning({ message: `File = ${documentNameFileName}, length = 0`, duration: 10 });
      return { url: '', type: '' };
    }

    const uploadResultl = await accountService.uploadPhoto(file, UploadFileTypes.IdDocument);

    return { url: uploadResultl.absoluteUri, type: type };
  };

  const onFinish = async (values: any) => {
    const residentId = eiqSecureStore?.residentDetails?.id;
    const selectedAddressId = eiqSecureStore?.selectedAddressId;
    const selectedVacantAddressId = eiqSecureStore?.selectedVacantAddress?.id;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, values.carLicensePlate, values.carState);

    const idDocument = await uploadIdDocumentImage(values);

    const admitTurnbackVisitorBody = {
      idDocumentUrl: idDocument.url,
      idDocumentType: idDocument.type ? idDocument.type : null,
      entryType: 1,
      image: image,
      camera: camera,
      ...values,
      plateRecognitionId: plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilagesRevoked.current,
    };

    const successMessage = selectedAction.current === TurnBack ? 'The visitor has been turned back' : 'The visitor has been admitted';

    if (isVacantAddress && selectedVacantAddressId) {
      await guestService.admitVisitorToVacantAddress(selectedVacantAddressId, admitTurnbackVisitorBody);
      notification.success({ message: successMessage });
      setVacantAddressSearchFilter('');
    }
    if (!isVacantAddress && residentId && selectedAddressId) {
      if (selectedAction.current === Admit) {
        await guestService.admitVisitor(residentId, selectedAddressId, admitTurnbackVisitorBody);
        notification.success({ message: successMessage });
      } else {
        admitTurnbackVisitorBody.reason = turnbackReason.current;
        await guestService.turnbackVisitor(residentId, selectedAddressId, admitTurnbackVisitorBody);
        notification.success({ message: successMessage });
      }
      setResidentListSearchFilter('');
    }
    tableContext.setSelectedRow(null);

    if (isVacantAddress) {
      eiqSecureStore.setAdmitFlow(AdmitGuestEnum.ADD);
    } else {
      eiqSecureStore.setAdmitFlow(null);
      eiqSecureStore.setIsAdmitFlowProcessed();
    }

    plateRecognitionContext.clear();
    resetState();
  };

  const admitGuest = () => {
    selectedAction.current = Admit;
    form.submit();
  };

  const handleClickAdmit = async () => {
    const licensePlate = form.getFieldValue('carLicensePlate');

    if (featureStore.isCitationsEnabled) {
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      isDrivingPrivilagesRevoked.current = result.isDrivingPrivilegesRevoked;

      if (result.isDrivingPrivilegesRevoked) {
        const initData = {
          drivingPrivilageData: result,
          isVacantAddress: isVacantAddress,
        };

        drivingPrivilagesRevokeModal.setModalInitData(initData);
        drivingPrivilagesRevokeModal.showModal();
        return;
      }
    }

    admitGuest();
  };

  const handleClickCancel = () => {
    resetState();
    eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
  };

  const handleVisitorType = (e: RadioChangeEvent) => {
    setVisitorType(e.target.value);
  };

  const contactsModal = useModal();

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (isSuccess) {
        if (type === Admit) {
          admitGuest();
        }
        if (type === TurnBack) {
          turnbackModal.showModal();
        }
      }
    },
  });

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, reason) => {
      if (isSuccessSaved) {
        turnbackReason.current = reason;
        selectedAction.current = TurnBack;
        form.submit();
      }
    },
  });

  const handleOnValuesChange = (_: any, values: any) => {
    if (
      !values.gate ||
      !values.carLicensePlate ||
      !values.carState ||
      !values.type ||
      (isServiceGuest(values.type) && !values.companyName) ||
      (!isServiceGuest(values.type) && (!values.firstName || !values.lastName))
    ) {
      setIsButtonsDisabled(true);
      return;
    }
    setIsButtonsDisabled(false);
    setFormValues(values);
  };

  useEffect(() => {
    if (!form) {
      setIsButtonsDisabled(true);
      return;
    }
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);

    if (isServiceGuest(values.type)) {
      const field = form.getFieldInstance('companyName');
      field?.focus();
    } else {
      const field = form.getFieldInstance('firstName');
      field?.focus();
    }
  }, [form, visitorType]);

  const selectedGate = configurationStore.configuration?.gates.find((gate) => gate.name === formValues?.gate);
  const routeOrigin = createRouteOrigin(selectedGate?.latitude, selectedGate?.longitude);
  const printAddress = isVacantAddress ? eiqSecureStore.selectedVacantAddress : tableContext.getSelectedRow()?.address;
  const { city, state } = configurationStore.communitySettings ?? {};
  const formattedPrintAddress = formatAddressLabel(printAddress);
  const routeDestination = city && state && formattedPrintAddress ? `${state}, ${city}, ${formattedPrintAddress}` : undefined;

  const { printRef, route, handlePrint } = useGuestPrintInstruction({
    isMapsLoaded: isLoaded,
    origin: routeOrigin,
    destination: routeDestination,
    fastAccessEnabled: false,
    obtainFastAccessPass: () => Promise.resolve(''),
  });

  const admitActionsBtn = (
    <>
      <Button type="default" icon={<PrintIcon />} size={'middle'} onClick={handlePrint} className="mobile-btn" disabled={isButtonsDisabled}>
        Print directions
      </Button>
      {!isVacantAddress && (
        <>
          <Button onClick={handleClickCancel}>Cancel</Button>
          <Button
            className="important-btn mobile-btn"
            icon={<TurnbackIcon />}
            onClick={turnbackModal.showModal}
            disabled={isButtonsDisabled}>
            Turnback
          </Button>
          <Button className="secondary-btn mobile-btn" icon={<PhoneIcon />} onClick={contactsModal.showModal}>
            Dial
          </Button>
        </>
      )}
      <Button
        type="primary"
        className="primary-btn mobile-btn"
        icon={<AdmitIcon />}
        onClick={handleClickAdmit}
        disabled={isButtonsDisabled}>
        Approve
      </Button>
    </>
  );

  const handleClickBack = () => {
    eiqSecureStore.setAdmitFlow(isMobileOrTable && !isVacantAddress ? AdmitGuestEnum.SELECT_GUEST : null);
  };

  const getGuestName = () => {
    if (isServiceGuest(formValues?.type)) {
      return formValues?.companyName;
    } else {
      return `${formValues?.firstName} ${formValues?.lastName}`;
    }
  };
  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  const onIdDocumentRecognized = (document: any) => {
    if (!document) {
      return;
    }

    form.setFieldValue('firstName', document.firstName);
    form.setFieldValue('lastName', document.lastName);

    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  };

  const onUpdateFromCameraClicked = () => {
    form.setFieldValue('firstName', '');
    form.setFieldValue('lastName', '');

    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  };

  return (
    <>
      <EiqSecureGuestPrintInstruction
        ref={printRef}
        address={printAddress}
        gate={formValues?.gate}
        guestLicense={formValues.carLicensePlate}
        guestName={getGuestName()}
        rules={configurationStore.configuration?.default.communityRules}
        communityName={configurationStore.configuration?.default.communityName ?? ''}
        routeOriginAddressString={route?.addressFrom}
        route={route}
        isMinimize={(route?.steps.length ?? 0) > 7}
        speedLimit={configurationStore.configuration?.default.speedLimit}
        speedLimitImageUrl={configurationStore.configuration?.default?.speedLimitImageUrl}
      />
      <div className="gust-add-visitor column-20">
        <Row justify="space-between">
          <Col>
            <div className="space-12">
              <div className="breadcrumb-wrapper">
                {!isVacantAddress ||
                  (isVacantAddress && isMobileOrTable && (
                    <Button icon={<BackIcon />} onClick={handleClickBack}>
                      Back
                    </Button>
                  ))}
                <Breadcrumb
                  separator="–"
                  items={[
                    {
                      title: ' Guests',
                    },
                  ]}
                />
              </div>
              <span className="sub-title">
                {formatAddressLabel(isVacantAddress ? eiqSecureStore.selectedVacantAddress : eiqSecureStore.getSelectedAddress())}
              </span>
            </div>
          </Col>
          <Col>
            <Space size={8} className="buttons-font-16">
              {!isMobileOrTable && admitActionsBtn}
            </Space>
          </Col>
        </Row>
        <div className="container-row">
          <Row gutter={32}>
            {isInfoSectionEnabled() && (
              <Col lg={8} sm={24} className="instructions-wrapper">
                {featureStore.isDriveIdEnabled && (
                  <DriveId
                    onUpdateFromCamera={onUpdateFromCameraClicked}
                    onImageRecognized={onIdDocumentRecognized}
                    ref={idDocumentRef}
                    idDocument={null}
                  />
                )}
                {featureStore.isPlateScanEnabled && image && (
                  <div className="instructions-item">
                    <span className="title">PlateScan ({camera})</span>
                    <Image src={image} alt="License Plate" loading="lazy" />
                  </div>
                )}
              </Col>
            )}
            <Col lg={isInfoSectionEnabled() ? 16 : 24} sm={24}>
              <FormWrapper form={form} onFinish={onFinish} layout="vertical" onValuesChange={handleOnValuesChange}>
                <Row gutter={{ lg: 32, md: 32, xs: 0 }}>
                  <Col lg={isInfoSectionEnabled() ? 8 : 4} sm={24}>
                    <Form.Item name="type" label="Type" rules={addVisitorValidation.type} initialValue={getUnlistedGuestDefaultType()}>
                      <Radio.Group onChange={handleVisitorType}>
                        <Space direction="vertical" size={14}>
                          <Radio value={GuestType.Guest}>Person</Radio>
                          <Radio value={GuestType.Service}>Service</Radio>
                          <Radio value={GuestType.Delivery}>Delivery</Radio>
                          <Radio value={GuestType.RideShare}>Ride share</Radio>
                          <Radio value={GuestType.UrgentService}>Urgent service</Radio>
                          <Radio value={GuestType.Medical}>Health & Medical</Radio>
                          <Radio value={GuestType.Employee}>Employee</Radio>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col lg={16} sm={24}>
                    <Row gutter={21}>
                      <Col span={isInfoSectionEnabled() ? 24 : 12}>
                        {isServiceGuest(visitorType) && (
                          <Form.Item name="companyName" label="Company name" rules={addVisitorValidation.companyName}>
                            <Input placeholder="Enter company name" />
                          </Form.Item>
                        )}
                        <Form.Item
                          name="firstName"
                          label="First name"
                          rules={!isServiceGuest(visitorType) ? addVisitorValidation.firstName : undefined}>
                          <Input placeholder="Enter first name" />
                        </Form.Item>
                        <Form.Item
                          name="lastName"
                          label="Last name"
                          rules={!isServiceGuest(visitorType) ? addVisitorValidation.lastName : undefined}>
                          <Input placeholder="Enter last name" />
                        </Form.Item>
                      </Col>
                      <Col span={isInfoSectionEnabled() ? 24 : 12}>
                        <Form.Item
                          name="carState"
                          label="License State"
                          initialValue={defaultLicenseState}
                          rules={addVisitorValidation.carState}>
                          <SelectState placeholder="Select a license state" popupClassName="so-select" />
                        </Form.Item>
                        <Form.Item
                          name="carLicensePlate"
                          initialValue={defaultLicensePlate}
                          label="License Plate"
                          rules={addVisitorValidation.carLicensePlate}>
                          <Input placeholder="Enter license plate" onPressEnter={handleClickAdmit} />
                        </Form.Item>
                        <Form.Item name="gate" label="Location (Gate)" rules={addVisitorValidation.gate} initialValue={defaultGate}>
                          <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </FormWrapper>
            </Col>
          </Row>
        </div>
      </div>
      {isMobileOrTable && (
        <Footer>
          <Space size={8}>{admitActionsBtn}</Space>
        </Footer>
      )}
      <ContactsModal title="Contacts" isOpen={contactsModal?.isOpenModal} onClose={contactsModal?.hideModal} />
      <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      <DrivingPrivilegesRevokeModal
        isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
        onClose={drivingPrivilagesRevokeModal?.hideModal}
        initData={drivingPrivilagesRevokeModal?.initData}
      />
    </>
  );
});
